import React, { Component, useContext, useEffect, useState, type ErrorInfo, type ReactNode } from "react";
import { useRouteError } from "react-router-dom";
import { getProviderInstance } from "@tolgee/react";
import { colors } from "../constants/GlobalStyles";
import { EventSentToMobileApp } from "../constants/GlobalTypesAndEnumsAndConstants";
import { serializeMobileAppEvent } from "../backend/utils/mobile-app-events";
import { trackError } from "../utils/analytics/track-error";
import { getAssetLoadDiagnostics } from "../utils/asset-load-diagnostics";
import { getPreloadRecoveryState } from "../utils/vite-preload-error-recovery";

// Last line of defence for the whole tree. Without it, an uncaught error in a
// provider above the router unmounts the React root and the user sees a plain
// white page with nothing to tap; inside the native app that page also looked
// healthy to the shell until it learned to probe the document. The router's
// own boundary catches route-level errors, but its default element is an
// unstyled "Unexpected Application Error!" page with no way to recover, so
// both paths render this screen and report through the same channel.
//
// This file must not depend on Chakra, Tolgee or any other provider: it has
// to render when those providers are the thing that crashed. Copy goes through
// Tolgee when its context is still alive (a route crash) and falls back to the
// English defaults when it is not (a provider crash).

const MAX_MESSAGE_LENGTH = 300;
const MAX_STACK_LENGTH = 2_000;

// The address the screen's sentence ends with, in every language. It is drawn as a link to write to it.
const SUPPORT_EMAIL_ADDRESS = "hello@jungleai.com";

export type RootRenderFailureSource = "root_boundary" | "router";

// A code-split chunk that vanished after a deployment rejects inside render
// and lands here too. `vite-preload-error-recovery.ts` already owns that case
// (reload once per build) and the native shell already receives it as
// `chunk_load_failed` from its injected `vite:preloadError` listener, so it is
// reported once at warning level and never as a second fatal issue.
// Production answers a removed hashed asset with the app's HTML, and WebKit
// rejects that import as "'text/html' is not a valid JavaScript MIME type".
const EXPLICIT_CHUNK_LOAD_FAILURE_PATTERN =
  /dynamically imported module|Importing a module script failed|Unable to preload CSS|Loading chunk|ChunkLoadError|'text\/html' is not a valid JavaScript MIME type/i;
// What React.lazy throws in V8, JavaScriptCore (iOS WebView) and SpiderMonkey
// when the recovery's preventDefault() lets a failed import resolve to
// undefined. An ordinary bug reading `.default` on undefined produces the same
// text, so these only count when a Vite preload failure was actually observed
// in this document moments before.
const UNDEFINED_DEFAULT_PATTERN = /reading 'default'|evaluating '[^']*\.default'|property "default"/i;
const PRELOAD_FAILURE_ATTRIBUTION_WINDOW_MS = 30_000;

let lastPreloadFailureObservedAt: number | null = null;
if (typeof window !== "undefined") {
  window.addEventListener("vite:preloadError", () => {
    lastPreloadFailureObservedAt = Date.now();
  });
}

export function isChunkLoadFailure(
  error: unknown,
  { preloadFailureObservedAt = lastPreloadFailureObservedAt, now = Date.now() } = {}
): boolean {
  if (!(error instanceof Error)) return false;
  if (error.name === "ChunkLoadError" || EXPLICIT_CHUNK_LOAD_FAILURE_PATTERN.test(error.message)) return true;
  // A clock that moved backwards must not keep the window open indefinitely.
  const wasPreloadFailureObservedRecently =
    preloadFailureObservedAt !== null &&
    now >= preloadFailureObservedAt &&
    now - preloadFailureObservedAt <= PRELOAD_FAILURE_ATTRIBUTION_WINDOW_MS;
  return wasPreloadFailureObservedRecently && UNDEFINED_DEFAULT_PATTERN.test(error.message);
}

/**
 * Tolgee's own `useTranslate` throws without a provider. Read the context
 * optionally so the screen still renders when the provider is what crashed.
 */
function useOptionalTranslate(): (key: string, defaultValue: string) => string {
  const tolgeeContext = useContext(getProviderInstance());
  return (key, defaultValue) => {
    try {
      const translated = tolgeeContext?.tolgee?.t(key, defaultValue);
      return typeof translated === "string" && translated.length > 0 ? translated : defaultValue;
    } catch {
      return defaultValue;
    }
  };
}

function countErrorForNativeHealthProbe(): void {
  // The native shell's foreground probe reads this counter to tell a crashed
  // document from one that is merely still mounting.
  const errors = (window as Window & { __JUNGLE_WEB_CONTENT_ERRORS__?: { count?: number } }).__JUNGLE_WEB_CONTENT_ERRORS__;
  if (errors && typeof errors.count === "number") {
    errors.count += 1;
  }
}

function describeError(error: unknown): { message: string; stack: string } {
  if (error instanceof Error) {
    return {
      message: (error.message || error.name || "Unknown render error").slice(0, MAX_MESSAGE_LENGTH),
      stack: (error.stack || "").slice(0, MAX_STACK_LENGTH),
    };
  }
  let message = "Unknown render error";
  try {
    message = typeof error === "string" ? error : JSON.stringify(error) || message;
  } catch {
    // Circular or exotic values keep the generic message.
  }
  return { message: message.slice(0, MAX_MESSAGE_LENGTH), stack: "" };
}

function firstRouteSegment(pathname: string): string {
  const match = /^\/?([^/?#]*)/.exec(pathname);
  return (match?.[1] ?? "").replace(/[^A-Za-z0-9_-]/g, "").slice(0, 40);
}

/**
 * Report a root render failure to Sentry and, inside the native app, to the
 * shell (`web_content_error` with kind `render_error`, the same message the
 * shell's injected listener uses for uncaught errors). Never throws.
 */
export function reportRootRenderFailure(
  error: unknown,
  { source, componentStack }: { source: RootRenderFailureSource; componentStack?: string | null }
): void {
  const { message, stack } = describeError(error);
  const isChunkFailure = isChunkLoadFailure(error);
  const recovery = isChunkFailure ? getPreloadRecoveryState() : null;
  trackError(error instanceof Error ? error : new Error(message), {
    scope: "root-render",
    level: isChunkFailure ? "warning" : "fatal",
    fingerprint: isChunkFailure
      ? ["root-render-failure", "chunk_load_failed"]
      : ["root-render-failure", source, message.slice(0, 120)],
    tags: { render_failure_source: source, render_failure_kind: isChunkFailure ? "chunk_load_failed" : "render_error" },
    extra: {
      componentStack: componentStack?.slice(0, MAX_STACK_LENGTH) ?? null,
      ...(recovery ? { asset_load: {
        ...getAssetLoadDiagnostics(error),
        recovery_decision: recovery.recoveryDecision,
        reload_requested: recovery.reloadRequested,
        recovery_pending: recovery.pendingRecovery !== null,
        preload_errors_observed: recovery.preloadErrorsObserved,
      } } : {}),
    },
  });
  try {
    countErrorForNativeHealthProbe();
    if (isChunkFailure) return;
    window.ReactNativeWebView?.postMessage(
      serializeMobileAppEvent(
        EventSentToMobileApp.WEB_CONTENT_ERROR,
        JSON.stringify({
          kind: "render_error",
          message,
          stack,
          route: firstRouteSegment(window.location.pathname),
          readyState: document.readyState,
          source,
          line: null,
          column: null,
        })
      )
    );
  } catch {
    // Diagnostics must never keep the fallback from rendering.
  }
}

export function RootRenderFailureScreen({ onReload }: { onReload?: () => void }) {
  const reload = onReload ?? (() => window.location.reload());
  const t = useOptionalTranslate();
  const [isReloadButtonHovered, setIsReloadButtonHovered] = useState(false);
  const failureBody = t(
    "root_render_failure_body",
    "the page hit an error. reloading usually fixes it. if it keeps happening, email hello@jungleai.com"
  );
  // The sentence is one translated string; the address inside it becomes the link. A translation without
  // the address is shown as it is.
  const indexOfSupportEmailAddress = failureBody.indexOf(SUPPORT_EMAIL_ADDRESS);
  useEffect(() => {
    // Inside the native app the startup cover stays up until the page reports
    // readiness. A crash before that point would leave this screen and its
    // reload button hidden underneath, so the fallback reports readiness
    // itself; on a healthy shell this is a harmless repeat.
    try {
      window.ReactNativeWebView?.postMessage(serializeMobileAppEvent(EventSentToMobileApp.LOADED_PAGE));
    } catch {
      // The screen is still usable in a plain browser.
    }
  }, []);
  return (
    <div
      role="alert"
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        boxSizing: "border-box",
        backgroundColor: colors.surface.offWhite,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: "480px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          gap: "16px",
        }}
      >
        <h1 style={{ margin: 0, fontSize: "24px", lineHeight: 1.25, fontWeight: 600, color: colors.text.header }}>
          {t("root_render_failure_title", "something went wrong")}
        </h1>
        <p style={{ margin: 0, fontSize: "16px", lineHeight: "24px", color: colors.text.body }}>
          {indexOfSupportEmailAddress < 0 ? (
            failureBody
          ) : (
            <>
              {failureBody.slice(0, indexOfSupportEmailAddress)}
              <a
                href={`mailto:${SUPPORT_EMAIL_ADDRESS}`}
                style={{ color: colors.primaryAction.default, textDecoration: "underline" }}
              >
                {SUPPORT_EMAIL_ADDRESS}
              </a>
              {failureBody.slice(indexOfSupportEmailAddress + SUPPORT_EMAIL_ADDRESS.length)}
            </>
          )}
        </p>
        <button
          type="button"
          onClick={reload}
          onMouseEnter={() => setIsReloadButtonHovered(true)}
          onMouseLeave={() => setIsReloadButtonHovered(false)}
          style={{
            marginTop: "8px",
            minWidth: "200px",
            padding: "12px 24px",
            borderRadius: "100px",
            border: "none",
            // The product's primary button (components/buttons/Button, which needs Chakra and so cannot be
            // used here): the solid green fill with a white label, 56px tall.
            backgroundColor: isReloadButtonHovered ? colors.primaryAction.fillHover : colors.primaryAction.fill,
            color: colors.text.white,
            fontFamily: "inherit",
            fontSize: "20px",
            lineHeight: "32px",
            fontWeight: 500,
            cursor: "pointer",
          }}
        >
          {t("root_render_failure_reload", "reload")}
        </button>
      </div>
    </div>
  );
}

type RootErrorBoundaryState = { hasRenderFailed: boolean };

/** Wraps `<App />` so a crash in any provider still leaves the user a reload. */
export class RootErrorBoundary extends Component<{ children: ReactNode }, RootErrorBoundaryState> {
  state: RootErrorBoundaryState = { hasRenderFailed: false };

  static getDerivedStateFromError(): RootErrorBoundaryState {
    return { hasRenderFailed: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    reportRootRenderFailure(error, { source: "root_boundary", componentStack: info.componentStack });
  }

  render(): ReactNode {
    return this.state.hasRenderFailed ? <RootRenderFailureScreen /> : this.props.children;
  }
}

/** `errorElement` for the root route: replaces React Router's default page. */
export function RouteRenderFailureElement() {
  const error = useRouteError();
  useEffect(() => {
    reportRootRenderFailure(error, { source: "router" });
  }, [error]);
  return <RootRenderFailureScreen />;
}
